/**
 * Miracles Protocol coaching vocabulary, shared by the API (validation,
 * scoring) and the coach SPA (onboarding options, copy). Keys are stable
 * storage values; labels come from the product blueprint's copy deck
 * (SC-05..SC-10). Never rename a key: rows and analytics store it.
 */

export const COACHING_FOCUS_KEYS = [
  'money',
  'business',
  'career',
  'relationships',
  'wellness',
  'confidence',
  'spiritual',
  'lifestyle',
  'family',
  'other',
] as const;
export type CoachingFocusCategory = (typeof COACHING_FOCUS_KEYS)[number];
export const COACHING_FOCUS_LABELS: Record<CoachingFocusCategory, string> = {
  money: 'Money & Wealth',
  business: 'Business Success',
  career: 'Career',
  relationships: 'Love & Relationships',
  wellness: 'Health & Wellness',
  confidence: 'Confidence',
  spiritual: 'Spiritual Growth',
  lifestyle: 'Lifestyle',
  family: 'Family',
  other: 'Other',
};

export const COACHING_BLOCKER_KEYS = [
  'fear',
  'procrastination',
  'clarity',
  'self_doubt',
  'low_energy',
  'inconsistency',
  'money_mindset',
  'distraction',
  'other',
] as const;
export type CoachingBlocker = (typeof COACHING_BLOCKER_KEYS)[number];
export const COACHING_BLOCKER_LABELS: Record<CoachingBlocker, string> = {
  fear: 'Fear',
  procrastination: 'Procrastination',
  clarity: 'Lack of clarity',
  self_doubt: 'Self-doubt',
  low_energy: 'Low energy',
  inconsistency: 'Inconsistent action',
  money_mindset: 'Money mindset',
  distraction: 'Distraction',
  other: 'Other',
};

export const COACHING_RITUAL_MINUTES = [5, 10, 15, 20] as const;
export type CoachingRitualMinutes = (typeof COACHING_RITUAL_MINUTES)[number];

export const COACHING_RITUAL_MODULE_KEYS = [
  'affirmations',
  'visualization',
  'breathwork',
  'gratitude',
  'journaling',
  'frequencies',
  'astrology',
  'biorhythm',
] as const;
export type CoachingRitualModule = (typeof COACHING_RITUAL_MODULE_KEYS)[number];
export const COACHING_RITUAL_MODULE_LABELS: Record<CoachingRitualModule, string> = {
  affirmations: 'Affirmations',
  visualization: 'Visualization',
  breathwork: 'Breathwork',
  gratitude: 'Gratitude',
  journaling: 'Journaling',
  frequencies: 'Frequencies',
  astrology: 'Astrology',
  biorhythm: 'Biorhythm',
};

export const COACHING_STYLE_KEYS = [
  'balanced',
  'strategic',
  'motivational',
  'gentle',
  'spiritual',
] as const;
export type CoachingStyle = (typeof COACHING_STYLE_KEYS)[number];
export const COACHING_STYLES: Record<CoachingStyle, { label: string; description: string }> = {
  balanced: { label: 'Balanced', description: 'Supportive, practical, and focused.' },
  strategic: { label: 'Strategic', description: 'Direct, logical, and action-oriented.' },
  motivational: { label: 'Motivational', description: 'Positive, energetic, and momentum-driven.' },
  gentle: { label: 'Gentle', description: 'Calm, patient, and encouraging.' },
  spiritual: { label: 'Spiritual', description: 'Reflective, intuitive, and purpose-centered.' },
};

export const COACHING_NEED_KEYS = [
  'accountability',
  'focus',
  'motivation',
  'confidence',
  'strategy',
  'wealth_mindset',
  'emotional_support',
] as const;
export type CoachingNeed = (typeof COACHING_NEED_KEYS)[number];
export const COACHING_NEED_LABELS: Record<CoachingNeed, string> = {
  accountability: 'Accountability',
  focus: 'Focus',
  motivation: 'Motivation',
  confidence: 'Confidence',
  strategy: 'Strategy',
  wealth_mindset: 'Wealth mindset',
  emotional_support: 'Emotional support',
};

/**
 * Practices a member completes inside the app. MINDSET practices feed the
 * Success Score's Mindset component, ALIGNMENT practices its Alignment
 * component. Every kind here has a screen that records it; an unknown string
 * is ignored by the score, so a new practice must be listed before it counts.
 */
export const COACHING_MINDSET_PRACTICES = [
  'affirmation',
  'journal',
  'gratitude',
  'visualization',
  'three_six_nine',
  'vision_board',
  'manifestation',
] as const;
export type CoachingMindsetPractice = (typeof COACHING_MINDSET_PRACTICES)[number];

export const COACHING_ALIGNMENT_PRACTICES = [
  'vedic_guidance',
  'breathwork',
  'frequency',
  'biorhythm',
  'mantra',
  'meditation',
] as const;
export type CoachingAlignmentPractice = (typeof COACHING_ALIGNMENT_PRACTICES)[number];

/** Practices a client may report. */
export const COACHING_PRACTICES = [
  ...COACHING_MINDSET_PRACTICES,
  ...COACHING_ALIGNMENT_PRACTICES,
] as const;
export type CoachingPractice = (typeof COACHING_PRACTICES)[number];

/** Recorded by the server as a side effect, never accepted from a client. */
export const COACHING_SYSTEM_PRACTICES = ['goal_progress'] as const;
export type CoachingSystemPractice = (typeof COACHING_SYSTEM_PRACTICES)[number];

export const COACHING_GOAL_STATUSES = ['active', 'achieved', 'archived'] as const;
export type CoachingGoalStatus = (typeof COACHING_GOAL_STATUSES)[number];

export const COACHING_MILESTONE_STATUSES = ['pending', 'done'] as const;
export type CoachingMilestoneStatus = (typeof COACHING_MILESTONE_STATUSES)[number];

export const COACHING_ACTION_STATUSES = ['pending', 'done', 'partial', 'skipped'] as const;
export type CoachingActionStatus = (typeof COACHING_ACTION_STATUSES)[number];

export const COACHING_ACTION_SOURCES = ['ai', 'user', 'system'] as const;
export type CoachingActionSource = (typeof COACHING_ACTION_SOURCES)[number];

/**
 * Client-reported analytics events (blueprint Part III §18). Server-side
 * facts (signup, subscription) are recorded by the server, not posted.
 */
export const COACHING_EVENT_NAMES = [
  'onboarding_started',
  'onboarding_step_completed',
  'assessment_completed',
  'paywall_viewed',
  'home_viewed',
  'morning_ritual_started',
  'morning_ritual_completed',
  'coach_message_sent',
  'coach_action_saved',
  'affirmation_played',
  'journal_entry_created',
  'breathwork_completed',
  'frequency_played',
  'evening_review_completed',
  'weekly_review_completed',
  'success_score_viewed',
  'chart_viewed',
  'vedic_guidance_viewed',
  'biorhythm_viewed',
  'three_six_nine_completed',
  'vision_board_updated',
  'mantra_completed',
  'meditation_completed',
  'manifestation_completed',
  'coach_prompt_used',
  'invite_link_copied',
  'invite_link_shared',
  'content_shared',
  'community_opened',
  'program_started',
  'program_day_completed',
  'program_completed',
  'program_left',
] as const;
export type CoachingEventName = (typeof COACHING_EVENT_NAMES)[number];

/** Shown wherever an astrology or guidance score is displayed (blueprint §4, SC-28). */
export const COACHING_ASTRO_DISCLAIMER =
  'Astrology is provided as a spiritual and reflective framework, not as a guarantee or scientific prediction.';

/** A day's morning ritual (SC-14) or evening review (SC-15). */
export const COACHING_RITUAL_KINDS = ['morning', 'evening'] as const;
export type CoachingRitualKind = (typeof COACHING_RITUAL_KINDS)[number];

/** Journal entry kinds. `evening` entries are written by the evening review, never posted directly. */
export const COACHING_JOURNAL_KINDS = ['free', 'prompt', 'gratitude', 'evening'] as const;
export type CoachingJournalKind = (typeof COACHING_JOURNAL_KINDS)[number];

export const COACHING_AFFIRMATION_SOURCES = ['ai', 'user', 'library'] as const;
export type CoachingAffirmationSource = (typeof COACHING_AFFIRMATION_SOURCES)[number];

/** Master Kala's persona id on the assistant module. */
export const MASTER_KALA_PERSONA_ID = 'master-kala';

/** How the member feels at the evening review (SC-15), and the 1-5 mood each counts as. */
export const COACHING_FEELINGS = [
  'energized',
  'happy',
  'calm',
  'neutral',
  'tired',
  'stressed',
  'frustrated',
] as const;
export type CoachingFeeling = (typeof COACHING_FEELINGS)[number];
export const COACHING_FEELING_LABELS: Record<CoachingFeeling, string> = {
  energized: 'Energized',
  happy: 'Happy',
  calm: 'Calm',
  neutral: 'Neutral',
  tired: 'Tired',
  stressed: 'Stressed',
  frustrated: 'Frustrated',
};
export const COACHING_FEELING_MOOD: Record<CoachingFeeling, number> = {
  energized: 5,
  happy: 5,
  calm: 4,
  neutral: 3,
  tired: 2,
  stressed: 2,
  frustrated: 1,
};

/**
 * Release 1 Phase 3 — plans and entitlements.
 *
 * `free` is the absence of an entitlement, never a row: a member with no live
 * window IS free, so the paid tiers are their own list. Ranked lowest first;
 * `COACHING_TIER_RANK` is what "highest live tier wins" reads.
 */
export const COACHING_TIERS = ['free', 'pro', 'elite'] as const;
export type CoachingTier = (typeof COACHING_TIERS)[number];
export const COACHING_TIER_RANK: Record<CoachingTier, number> = { free: 0, pro: 1, elite: 2 };
export const COACHING_TIER_LABELS: Record<CoachingTier, string> = {
  free: 'Free',
  pro: 'Success Pro',
  elite: 'Success Elite',
};

/**
 * The tier names a tenant chose (`coaching.tier_labels`), with the defaults
 * standing in for anything blank. Every member- or staff-facing mention of a
 * tier goes through this; `COACHING_TIER_LABELS` is only the default.
 */
export function coachingTierLabels(
  cfg?: { tier_labels?: Partial<Record<'pro' | 'elite', string>> | null } | null,
): Record<CoachingTier, string> {
  const pro = cfg?.tier_labels?.pro?.trim() || COACHING_TIER_LABELS.pro;
  const elite = cfg?.tier_labels?.elite?.trim() || COACHING_TIER_LABELS.elite;
  return { free: COACHING_TIER_LABELS.free, pro, elite };
}

/**
 * Copy written against the default names ("Success Pro unlocks this"),
 * reworded for a tenant that renamed its tiers. The catalog's lock lines and
 * the feature summaries are authored once with the defaults and pass through
 * here on the way out.
 */
export function withTierLabels(text: string, labels: Record<CoachingTier, string>): string {
  return text
    .split(COACHING_TIER_LABELS.elite)
    .join(labels.elite)
    .split(COACHING_TIER_LABELS.pro)
    .join(labels.pro);
}

/** Tiers that are SOLD (a `coaching_plans` row exists for them). */
export const COACHING_PLAN_TIERS = ['pro', 'elite'] as const;
export type CoachingPlanTier = (typeof COACHING_PLAN_TIERS)[number];

/** How long one purchase of a plan runs. The interval arithmetic itself is SQL. */
export const COACHING_PLAN_PERIODS = ['monthly', 'annual'] as const;
export type CoachingPlanPeriod = (typeof COACHING_PLAN_PERIODS)[number];
export const COACHING_PLAN_PERIOD_LABELS: Record<CoachingPlanPeriod, string> = {
  monthly: 'Monthly',
  annual: 'Annual',
};

/** How an entitlement window came to exist. */
export const COACHING_ENTITLEMENT_SOURCES = ['purchase', 'admin_grant'] as const;
export type CoachingEntitlementSource = (typeof COACHING_ENTITLEMENT_SOURCES)[number];

/** Admin-editable `coaching_content` kinds (the typed editors on Admin → Coaching). */
export const COACHING_CONTENT_KINDS = [
  'affirmation',
  'prompt',
  'reading_copy',
  'breathwork',
  'audio',
  'mantra',
  'meditation',
  'manifestation',
  /** The prompt library: what a member can open a Master Kala conversation with. */
  'coach_prompt',
] as const;
export type CoachingContentKind = (typeof COACHING_CONTENT_KINDS)[number];

/**
 * The `reading_copy` key prefixes, i.e. which entry of the built-in chart prose
 * an override replaces. Must match the switch in `services/coaching/content.ts`.
 */
export const COACHING_READING_KINDS = [
  'lagna',
  'second_house',
  'eleventh_house',
  'yoga',
  'nakshatra',
  'transit_house',
  'dasha_wealth',
  'dasha_timing',
  'dasha_action',
] as const;
export type CoachingReadingKind = (typeof COACHING_READING_KINDS)[number];
export const COACHING_READING_KIND_LABELS: Record<CoachingReadingKind, string> = {
  lagna: 'Ascendant sign',
  second_house: 'Second house sign',
  eleventh_house: 'Eleventh house sign',
  yoga: 'Yoga',
  nakshatra: 'Moon nakshatra',
  transit_house: 'Transit house',
  dasha_wealth: 'Dasha: wealth',
  dasha_timing: 'Dasha: timing',
  dasha_action: 'Dasha: action',
};

/**
 * What a 402 `coaching_pro_required` was refused for. The coach app maps these
 * to the line it shows on the lock, so the server never sends member copy.
 */
export const COACHING_PRO_FEATURES = [
  'kala_chat',
  'library',
  'journal_reflection',
  'affirmation_generation',
  'action_suggestions',
  'chart',
  'guidance',
  'goal_limit',
  'evening_ai_summary',
  'streak_freeze',
  'daily_messages',
  'breathwork',
  'frequencies',
  'biorhythm',
  'mantras',
  'meditation',
  'karma',
  'aura',
  'palmistry',
  'programs',
] as const;
export type CoachingProFeature = (typeof COACHING_PRO_FEATURES)[number];

// ── Phase 4: the Align and Manifest tools ──────────────────────────────────

/** How the member wants to feel after a breathing session (SC-30). */
export const COACHING_BREATHWORK_OUTCOMES = [
  'calm',
  'focus',
  'energy',
  'confidence',
  'manifestation',
  'sleep',
] as const;
export type CoachingBreathworkOutcome = (typeof COACHING_BREATHWORK_OUTCOMES)[number];
export const COACHING_BREATHWORK_OUTCOME_LABELS: Record<CoachingBreathworkOutcome, string> = {
  calm: 'Calm',
  focus: 'Focused',
  energy: 'Energized',
  confidence: 'Confident',
  manifestation: 'Ready to manifest',
  sleep: 'Ready to sleep',
};

/** The listening intentions a frequency track is filed under (SC-31). */
export const COACHING_FREQUENCY_CATEGORIES = [
  'abundance',
  'focus',
  'relaxation',
  'sleep',
  'confidence',
  'meditation',
  'spiritual',
] as const;
export type CoachingFrequencyCategory = (typeof COACHING_FREQUENCY_CATEGORIES)[number];
export const COACHING_FREQUENCY_CATEGORY_LABELS: Record<CoachingFrequencyCategory, string> = {
  abundance: 'Abundance',
  focus: 'Focus',
  relaxation: 'Relaxation',
  sleep: 'Sleep',
  confidence: 'Confidence',
  meditation: 'Meditation',
  spiritual: 'Spiritual alignment',
};
/** Tile and player hues per built-in category (the design handoff's oklch hues). */
export const COACHING_FREQUENCY_CATEGORY_HUES: Record<CoachingFrequencyCategory, number> = {
  abundance: 75,
  focus: 170,
  relaxation: 150,
  sleep: 275,
  confidence: 35,
  meditation: 310,
  spiritual: 230,
};

/** A category as the member sees it: the tenant's own list, or the built-ins. */
export type CoachingFrequencyCategoryView = { key: string; label: string; hue: number | null };

// ── The content library (2026-10-13) ───────────────────────────────────────

export const COACHING_LIBRARY_KINDS = ['video', 'audio', 'image', 'document'] as const;
export type CoachingLibraryKind = (typeof COACHING_LIBRARY_KINDS)[number];
export const COACHING_LIBRARY_KIND_LABELS: Record<CoachingLibraryKind, string> = {
  video: 'Video',
  audio: 'Audio',
  image: 'Image',
  document: 'Document',
};
/** Uploads the API accepts per kind; a video is link-only (Vimeo, YouTube, a direct file). */
export const COACHING_LIBRARY_UPLOAD_KINDS: readonly CoachingLibraryKind[] = [
  'audio',
  'image',
  'document',
];

export type CoachingLibraryCategoryView = { key: string; label: string };

/** The library categories the tenant defined (`coaching.library_categories`); none by default. */
export function coachingLibraryCategories(
  cfg?: { library_categories?: Array<{ key: string; label: string }> | null } | null,
): CoachingLibraryCategoryView[] {
  return (cfg?.library_categories ?? [])
    .filter((c) => c.key?.trim() && c.label?.trim())
    .map((c) => ({ key: c.key.trim(), label: c.label.trim() }));
}

/** A hue for a category the admin gave none: stable per key, spread around the wheel. */
export function frequencyCategoryHue(key: string): number {
  let h = 0;
  for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

/**
 * The categories in force: what the tenant saved in `coaching.frequency_categories`
 * (blank rows dropped), else the seven built-ins with their hues.
 */
export function coachingFrequencyCategories(
  cfg?: {
    frequency_categories?: Array<{ key: string; label: string; hue?: number | null }> | null;
  } | null,
): CoachingFrequencyCategoryView[] {
  const own = (cfg?.frequency_categories ?? []).filter((c) => c.key?.trim() && c.label?.trim());
  if (own.length) {
    return own.map((c) => ({
      key: c.key.trim(),
      label: c.label.trim(),
      hue: typeof c.hue === 'number' ? c.hue : null,
    }));
  }
  return COACHING_FREQUENCY_CATEGORIES.map((key) => ({
    key,
    label: COACHING_FREQUENCY_CATEGORY_LABELS[key],
    hue: COACHING_FREQUENCY_CATEGORY_HUES[key],
  }));
}

/** Shown on every frequency screen (blueprint SC-31). */
export const COACHING_FREQUENCY_DISCLAIMER =
  'Frequency tracks are intended for relaxation, meditation and spiritual wellness, and are not medical treatment.';

/** The 369 practice (SC-25): one affirmation, written 3, 6 and 9 times through the day. */
export const COACHING_THREE_SIX_NINE_SESSIONS = ['morning', 'afternoon', 'evening'] as const;
export type CoachingThreeSixNineSession = (typeof COACHING_THREE_SIX_NINE_SESSIONS)[number];
export const COACHING_THREE_SIX_NINE_COUNTS: Record<CoachingThreeSixNineSession, number> = {
  morning: 3,
  afternoon: 6,
  evening: 9,
};
export const COACHING_THREE_SIX_NINE_LABELS: Record<CoachingThreeSixNineSession, string> = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

/** Pictures a member may keep on their vision board at once. */
export const COACHING_VISION_BOARD_LIMIT = 24;

/** The five weekly review questions (SC-39), in order. Keys are stored in `answers`. */
export const COACHING_WEEKLY_REVIEW_QUESTIONS = [
  { key: 'biggestWin', label: 'What was your biggest win?' },
  { key: 'progressSource', label: 'What created the most progress?' },
  { key: 'heldBack', label: 'What held you back?' },
  { key: 'learned', label: 'What did you learn?' },
  { key: 'nextWeek', label: 'What matters most next week?' },
] as const;
export type CoachingWeeklyReviewKey = (typeof COACHING_WEEKLY_REVIEW_QUESTIONS)[number]['key'];
